import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { rupees, fmtDate } from "@/lib/helpers";
import { StatusBadge, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, User, Receipt, Coins } from "@phosphor-icons/react";

function Stat({ label, value }) {
  return (
    <div className="bg-[#F8F6F0] rounded-md px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-[#8A8A8A]">{label}</div>
      <div className="text-lg font-medium text-[#1A1A1A] tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => { api.get(`/customers/${id}`).then(({ data }) => setD(data)).catch(() => {}); }, [id]);
  if (!d) return <div className="p-8 text-[#8A8A8A]">Loading customer…</div>;
  const c = d.customer;

  return (
    <div className="p-6 lg:p-8 fade-in">
      <Button variant="ghost" onClick={() => navigate("/customers")} className="mb-4 text-[#8A6D2F]" data-testid="back-customers"><ArrowLeft size={16} className="mr-1" /> Customers</Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="p-6 bg-white border-[#E6E2D6] shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-full bg-[#C5A059] text-white flex items-center justify-center text-xl font-medium">{c.name.charAt(0)}</div>
            <div>
              <div className="font-display text-2xl font-semibold text-[#1A1A1A]">{c.name}</div>
              <div className="text-sm text-[#8A8A8A]">{c.mobile}</div>
            </div>
          </div>
          <div className="text-sm space-y-1.5 text-[#4A4A4A]">
            <div><span className="text-[#8A8A8A]">Email:</span> {c.email || "-"}</div>
            <div><span className="text-[#8A8A8A]">Address:</span> {c.address || "-"}</div>
            <div><span className="text-[#8A8A8A]">KYC:</span> {c.kyc_type ? `${c.kyc_type} — ${c.kyc_number}` : "-"}</div>
            <div><span className="text-[#8A8A8A]">GSTIN:</span> {c.gstin || "-"}</div>
          </div>
        </Card>
        <Card className="lg:col-span-2 p-6 bg-white border-[#E6E2D6] shadow-sm">
          <div className="text-sm font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2"><User size={16} className="text-[#C5A059]" /> 360° Snapshot</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Total Spent" value={rupees(d.total_spent)} />
            <Stat label="Purchases" value={d.total_purchases} />
            <Stat label="Schemes" value={d.enrollments.length} />
            <Stat label="Outstanding" value={rupees(c.outstanding || 0)} />
          </div>
        </Card>
      </div>

      <Tabs defaultValue="sales">
        <TabsList className="bg-[#F2EBE1]">
          <TabsTrigger value="sales" data-testid="tab-sales"><Receipt size={15} className="mr-1" /> Purchases</TabsTrigger>
          <TabsTrigger value="schemes" data-testid="tab-schemes"><Coins size={15} className="mr-1" /> Schemes</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="sales">
          <Card className="bg-white border-[#E6E2D6] shadow-sm mt-3">
            {d.sales.length === 0 ? <EmptyState label="No purchases yet" /> : (
              <Table>
                <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>{d.sales.map((s) => (
                  <TableRow key={s.id}><TableCell className="font-medium">{s.invoice_no}</TableCell><TableCell className="text-sm">{fmtDate(s.created_at)}</TableCell><TableCell className="text-right tabular-nums">{rupees(s.grand_total)}</TableCell><TableCell><StatusBadge status={s.status} /></TableCell></TableRow>
                ))}</TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="schemes">
          <Card className="bg-white border-[#E6E2D6] shadow-sm mt-3">
            {d.enrollments.length === 0 ? <EmptyState label="No scheme enrollments" /> : (
              <Table>
                <TableHeader><TableRow><TableHead>Account</TableHead><TableHead>Scheme</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Grams</TableHead><TableHead className="text-right">Redeem Balance</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>{d.enrollments.map((e) => (
                  <TableRow key={e.id}><TableCell className="font-medium">{e.account_no}</TableCell><TableCell>{e.scheme_name}</TableCell><TableCell className="text-right tabular-nums">{rupees(e.total_paid)}</TableCell><TableCell className="text-right tabular-nums">{Number(e.total_grams).toFixed(3)}</TableCell><TableCell className="text-right tabular-nums text-[#2D6A4F]" data-testid={`cust-redeem-bal-${e.account_no}`}>{e.redeem_balance > 0 ? rupees(e.redeem_balance) : "-"}</TableCell><TableCell><StatusBadge status={e.status} /></TableCell></TableRow>
                ))}</TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="ledger">
          <Card className="bg-white border-[#E6E2D6] shadow-sm mt-3">
            {d.ledger.length === 0 ? <EmptyState label="No ledger entries" /> : (
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Reference</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                <TableBody>{d.ledger.map((l) => (
                  <TableRow key={l.id} data-testid={`cust-ledger-${l.id}`}><TableCell className="text-sm">{fmtDate(l.at)}</TableCell><TableCell className="capitalize text-sm">{l.type.replace(/_/g, " ")}</TableCell><TableCell className="text-sm">{l.invoice_no || "-"}</TableCell><TableCell className={`text-right tabular-nums ${l.amount < 0 ? "text-[#2D6A4F]" : "text-[#1A1A1A]"}`}>{rupees(l.amount)}</TableCell></TableRow>
                ))}</TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
